<script setup>
// Page 3 — how Pandig is arranged for a portfolio: the four decision-shaped items, the
// ones that answer "how is this put together". The four operational ones ("what do I
// see and get") are the reference spec sheet below the seam — eight bodies of up to
// ~90 words cannot fit one phone screen, and FeatureDeck's own comment records that
// even five cards on a panel were the site's worst source of scroll fatigue. Every
// body survives verbatim either way.
//
// Scroll-scrubbed over a taller sticky section, same shape as the page before it: the
// heading and lead never hide, the rows resolve from DIM.
//
// Same register as the page before it — one ink frame, hairline rows — with the numbers
// kept, because 01–04 here and 05–08 below the seam are one list of eight split in two.
// The row grammar is deliberately the one BeheerdersDetail uses for the other half;
// what distinguishes the halves is the container (an ink frame with the panel's one
// hard shadow here, a plain rail there), not the row.
//
// The chips stay because they WORK: each one flips secondary → primary as its row
// resolves under the scrub, so scrolling this panel lights the arrangement up piece by
// piece. That is LandingHow's chip, the one the homepage kept when the decorative ones
// went — an icon earns its place here by carrying state, not by sitting next to a title.
import { ref, onMounted, onUnmounted } from "vue";
import { Users, CreditCard, Landmark, Eye } from "@lucide/vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, EASE, SCRUB, DIM } from "@/lib/motion";

const items = [
  {
    n: "01",
    icon: Users,
    term: "Één account, meerdere VvE's",
    body: "Je wisselt van organisatie zonder opnieuw in te loggen. Collega's nodig je per VvE uit, als bestuur of als eigenaar; een uitnodiging is veertien dagen geldig.",
  },
  {
    n: "02",
    icon: CreditCard,
    term: "Dezelfde stroom per VvE",
    body: "De appartementseigenaar geeft eenmalig een SEPA-machtiging af via een verificatiebetaling van één cent. Daarna incasseert Pandig per maand, kwartaal, half jaar of jaar — per eenheid in te stellen.",
  },
  {
    n: "03",
    icon: Landmark,
    term: "Gescheiden gehouden",
    body: "Elke VvE heeft een eigen Mollie-account op eigen naam, een eigen bankrekening en eigen machtigingen. Geen tussenrekening en geen gedeelde pot.",
  },
  {
    n: "04",
    icon: Eye,
    term: "Vóór de run al zichtbaar",
    body: "Per VvE zie je het verwachte totaal, de datum van de volgende run en per eenheid wat blokkeert: geen eigenaar, geen actieve incasso, geen geldige machtiging.",
  },
];

const STAGGER = 0.28;
const DURATION = 0.5;

const root = ref(null);
const cardEls = ref([]);

// Which chips have lit. Reduced motion starts them all lit, so the resting picture is
// the same one either way — an unlit chip means "not there yet", not "not available".
const activated = ref(items.map(() => reducedMotion));

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    gsap.set(cardEls.value, { opacity: DIM, y: 18 });

    const tl = gsap.timeline({ paused: true }).to(cardEls.value, {
      opacity: 1,
      y: 0,
      duration: DURATION,
      ease: EASE,
      stagger: STAGGER,
    });

    // A row's chip lights the moment that row's own tween finishes, so the flip reads as
    // the consequence of the row arriving rather than as a second effect beside it.
    const arrivesAt = items.map((_, i) => i * STAGGER + DURATION);

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
      // Only write the ref when a value actually flips: Vue re-writes the class
      // attribute on every assignment, and doing that on every scrub tick would force a
      // style recalc on all four chips for the whole scroll range. Same guard as
      // LandingHow, and the same reason.
      onUpdate: (self) => {
        const time = self.progress * tl.duration();
        const next = arrivesAt.map((at) => time >= at);
        if (next.some((lit, i) => lit !== activated.value[i])) activated.value = next;
      },
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section
    id="inrichting"
    ref="root"
    class="relative border-t-2 border-foreground bg-background"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[130vh] sm:h-[155vh]'"
    data-jump="settled"
    data-page-label="De inrichting"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-4 sm:py-14">
        <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
          Elke VvE een eigen geldstroom. Jij één werkplek.
        </h2>
        <p class="mt-2 max-w-3xl text-xs leading-snug text-muted-foreground sm:mt-4 sm:text-base sm:leading-relaxed">
          Pandig behandelt elke VvE als een eigen organisatie met een eigen geldstroom, en geeft jou daar één werkplek voor.
        </p>

        <ol class="mt-3 border-2 border-foreground bg-card shadow-[var(--shadow-hard)] sm:mt-9">
          <li
            v-for="(item, i) in items"
            :key="item.n"
            :ref="(el) => (cardEls[i] = el)"
            class="border-t-2 border-foreground/10 p-2.5 first:border-t-0 sm:grid sm:grid-cols-[auto_15rem_1fr] sm:items-start sm:gap-x-5 sm:p-6"
          >
            <!-- sm:contents dissolves this wrapper into the grid, so the chip+index and
                 the term become columns of their own from sm: up. On a phone they share
                 one line and the body takes the full width beneath them: four bodies of
                 this length cost a line each when they have to flow beside an index
                 column, and four lines is most of what the panel has left to give. -->
            <div class="flex items-center gap-2 sm:contents">
              <span class="flex items-center gap-2 sm:gap-2.5">
                <span
                  class="flex size-6 shrink-0 items-center justify-center border-2 border-foreground transition-colors duration-200"
                  :class="activated[i] ? 'bg-primary text-primary-foreground' : 'bg-secondary text-primary'"
                >
                  <component :is="item.icon" class="size-3.5" />
                </span>
                <span class="font-mono text-sm font-semibold tabular-nums text-primary sm:text-base">{{ item.n }}</span>
              </span>
              <h3 class="min-w-0 font-sans text-sm font-semibold leading-snug tracking-tight text-foreground sm:text-base">{{ item.term }}</h3>
            </div>
            <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-0 sm:text-sm sm:leading-relaxed">{{ item.body }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>
